import * as THREE from 'three';
import {Store} from "../../../Application/Store/Store";
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import {setKeyframeTo, setKeyframeToBegin, setSceneLoaded} from "../../Scene/Manager/SceneManager";
import {getTimes} from "../../Scene/Handler/AnimationHandler";
import {GRIDLOADED} from "../../../Application/Store/Actions/ViewportTypes";
import {GRID_ACTIVE} from "../../../Application/Store/Actions/ViewportTypes";
import {
    attachTeethToMandiGroup,
    cloneTeethGroup,
    stateSetup
} from "../../Scene/Handler/ComparativeViewHandler";

import {createOverlayTeeth} from "../../Scene/Handler/OverlayHandler";
import {toothSetupMaterial} from "../../Scene/Provider/MaterialProvider";
import { nextRenderTick } from "../../Scene/Manager/InitializationOrchestrator";


let gridHelpers = {};
let isGridActive = false;
const _tempCameraDirection = new THREE.Vector3();

const
    gridSize = 60,
    mainDivisions = 12,
    subDivisions = 60,
    mainColor = 0x666666,
    subColor = 0x555555,
    centerLineColor = 0x222222,
    maxAngle = Math.PI / 6,
    maxAngleSub = Math.PI / 10,
    opacityDiviseur = 0.5



/**
 * Crée les 6 grilles avec les axes pour chaque face du bounding box des dents.
 * @param {THREE.Scene} scene - La scène dans laquelle la grille sera ajoutée.
 */
export async function createGrids() {
    const Animation = Store.getState().AnimationReducer;
    if (!Animation.metas.pointPlanesJaw){
        Store.dispatch({
            type: GRIDLOADED,
            payload: true
        });


        Store.dispatch(setSceneLoaded());
        return;
    }
    stateSetup.setupReady = true;
    initPosition();
    await nextRenderTick();
    /* Création de nos points pour chaque face */
    const scene = threeEngine.getScene();
    if (!scene) {
        return;
    }
    const points = await getMaxGridPosition(scene)

    if (!points){
        return;
    }


    /* Création de nos normals*/
    const normals = [
        { name: 'bottom', normal: new THREE.Vector3(0, 0, 1), rotation: [Math.PI / 2, 0, 0], position: points["bottom"] },
        { name: 'top', normal: new THREE.Vector3(0, 0, -1), rotation: [-Math.PI / 2, 0, 0], position: points["top"] },
        { name: 'front', normal: new THREE.Vector3(0, 1, 0), rotation: [0, Math.PI / 2, 0], position: points["front"]},
        { name: 'back', normal: new THREE.Vector3(0, -1, 0), rotation: [0, -Math.PI / 2, 0] , position: points["back"]},
        { name: 'left', normal: new THREE.Vector3(1, 0, 0), rotation: [0, 0, Math.PI / 2] , position: points["left"]},
        { name: 'right', normal: new THREE.Vector3(-1, 0, 0), rotation: [0, 0, -Math.PI / 2], position: points["right"] },
    ];

    normals.forEach(({ name, normal, rotation, position }) => {

        /* Fonction permettant de créer notre grille */
        const createGridHelper = (size, divisions, color, opacity) => {
            const grid = new THREE.GridHelper(size, divisions, color, color);
            grid.rotation.set(...rotation);
            grid.position.set(position.x, position.y, position.z);
            grid.material.transparent = true;
            grid.material.opacity = opacity;
            grid.visible = false;
            scene.add(grid);
            return grid;
        };

        /* Création de nos deux grilles par face*/
        const mainGrid = createGridHelper(gridSize, mainDivisions, mainColor, 1.0);
        const subGrid = createGridHelper(gridSize, subDivisions, subColor, 0.4);


        /* Fonction de création de ligne pour les axes d'occlusions et sagittal */
        const createCenterLine = () => {
            const material = new THREE.MeshBasicMaterial({ color: centerLineColor, transparent: true });
            const geometry = new THREE.BoxGeometry(gridSize, 0.2, 0.2);
            const mesh = new THREE.Mesh(geometry, material);
            mesh.position.set(position.x, position.y, position.z);
            mesh.visible = false;
            return mesh;
        };

        /* Création et mise en place des axes en fonction de la face */
        const centerLineX = createCenterLine();
        const centerLineY = createCenterLine();

        if (normal.equals(new THREE.Vector3(0, 1, 0)) || normal.equals(new THREE.Vector3(0, -1, 0))) { // Face de devant et derrière
            centerLineX.rotation.set(0, 0, 0); // Axe d'occlusion
            centerLineY.rotation.set(0, Math.PI / 2, 0); // Axe sagittal
            scene.add(centerLineX);
            scene.add(centerLineY);
        } else if (normal.equals(new THREE.Vector3(1, 0, 0)) || normal.equals(new THREE.Vector3(-1, 0, 0))) { // Face de à gauche et à droite
            centerLineX.rotation.set(0, 0, Math.PI / 2); // Axe d'occlusion
            centerLineY.rotation.set(0, Math.PI / 2, 0); // Axe sagittal qu'on affiche pas
            scene.add(centerLineX);
        } else { // Face de en haut et en bas
            centerLineX.rotation.set(0, 0, 0); // Axe d'occlusion qu'on affiche pas
            centerLineY.rotation.set(0, 0, Math.PI / 2); // Axe sagittal
            scene.add(centerLineY);
        }

        gridHelpers[name] = { mainGrid, subGrid, centerLineX, centerLineY, normal };
    });

    await nextRenderTick();
    Store.dispatch(setKeyframeToBegin());

    Store.dispatch({
        type: GRIDLOADED,
        payload: true
    });


    Store.dispatch(setSceneLoaded());

}

/**
 * Met à jour la visibilité des grilles en fonction de l'alignement de la caméra.
 * @param {THREE.Scene} scene - La scène contenant les objets 3D.
 * @param {THREE.Camera} camera - La caméra utilisée pour la vue actuelle.
 */
export function GridUpdate(scene, camera) {
    if (!isGridActive) {
        hideAllGrids();
        return;
    }

    const layer = Store.getState().ViewportReducer.mainViewerLayer === "mandi" ? 1 : 0;
    camera.getWorldDirection(_tempCameraDirection);

    Object.entries(gridHelpers).forEach(([name, { mainGrid, subGrid, centerLineX, centerLineY, normal }]) => {
        const isCorrectView = checkCameraAlignment(name, _tempCameraDirection);
        setGridLayer([mainGrid, subGrid, centerLineX, centerLineY], layer);

        if (isCorrectView) {
            updateGridVisibility(mainGrid, subGrid, centerLineX, centerLineY, normal, _tempCameraDirection);
        } else {
            hideGrid(mainGrid, subGrid, centerLineX, centerLineY);
        }
    });

    if (Store.getState().ViewportReducer.viewportMode !== "single") {
        hideAllGrids();
    }
}

/**
 * Vérifie si l'alignement de la caméra permet de montrer la grille d'une face donnée.
 * @param {string} name - Le nom de la face (ex: 'bottom', 'top', etc.).
 * @param {THREE.Vector3} cameraDirection - La direction actuelle de la caméra.
 * @returns {boolean} - True si la caméra est alignée avec la face, false sinon.
 */
function checkCameraAlignment(name, cameraDirection) {
    if ((name === 'bottom' || name === 'top') && Math.abs(cameraDirection.z) > Math.abs(cameraDirection.x) && Math.abs(cameraDirection.z) > Math.abs(cameraDirection.y)) {
        return true;
    }
    if ((name === 'front' || name === 'back') && Math.abs(cameraDirection.y) > Math.abs(cameraDirection.x) && Math.abs(cameraDirection.y) > Math.abs(cameraDirection.z)) {
        return true;
    }
    if ((name === 'left' || name === 'right') && Math.abs(cameraDirection.x) > Math.abs(cameraDirection.y) && Math.abs(cameraDirection.x) > Math.abs(cameraDirection.z)) {
        return true;
    }
    return false;
}


/**
 * Définit la couche de visibilité des éléments donnés.
 * @param {Array<THREE.Object3D>} elements - Liste des objets 3D à modifier.
 * @param {number} layer - La couche à appliquer.
 */
function setGridLayer(elements, layer) {
    elements.forEach(element => element.layers.set(layer));
}


/**
 * Met à jour la visibilité des grilles en fonction de l'angle entre la caméra et la normale de la grille.
 * @param {THREE.GridHelper} mainGrid - La grille principale.
 * @param {THREE.GridHelper} subGrid - La grille secondaire.
 * @param {THREE.Mesh} centerLineX - L'axe central X.
 * @param {THREE.Mesh} centerLineY - L'axe central Y.
 * @param {THREE.Vector3} normal - La normale de la face de la grille.
 * @param {THREE.Vector3} cameraDirection - La direction de la caméra.
 */
function updateGridVisibility(mainGrid, subGrid, centerLineX, centerLineY, normal, cameraDirection) {
    mainGrid.visible = subGrid.visible = centerLineX.visible = centerLineY.visible = true;
    const angle = cameraDirection.angleTo(normal);
    const opacity = angle > maxAngle ? 0 : Math.max(0, 1 - Math.pow(angle / maxAngle, 2) * 4);
    const opacitySub = angle > maxAngle ? 0 : Math.max(0, 0.7 - Math.pow(angle / maxAngleSub, 2) * 4);

    subGrid.material.opacity = opacitySub * opacityDiviseur;
    mainGrid.material.opacity = opacity;
    centerLineX.material.opacity = opacity;
    centerLineY.material.opacity = opacity;
}


/** Permet de cacher totalement une grille  **/
/**
 * Cache une grille complète (grilles principale, secondaire et axes).
 * @param {THREE.GridHelper} mainGrid - La grille principale.
 * @param {THREE.GridHelper} subGrid - La grille secondaire.
 * @param {THREE.Mesh} centerLineX - L'axe central X.
 * @param {THREE.Mesh} centerLineY - L'axe central Y.
 */
function hideGrid(mainGrid, subGrid, centerLineX, centerLineY) {
    mainGrid.visible = subGrid.visible = centerLineX.visible = centerLineY.visible = false;
    mainGrid.material.opacity = subGrid.material.opacity = centerLineX.material.opacity = centerLineY.material.opacity = 0;
}


/**
 * Cache toutes les grilles de la scène.
 */
function hideAllGrids() {
    Object.values(gridHelpers).forEach(({ mainGrid, subGrid, centerLineX, centerLineY }) => {
        hideGrid(mainGrid, subGrid, centerLineX, centerLineY);
    });
}



/**
 * Active ou désactive la grille.
 */
export function toggleGrid(){
    isGridActive = !isGridActive;
    Store.dispatch({
        type: GRID_ACTIVE,
        payload: isGridActive
    });
}

/**
 * Nettoie les ressources du grid pour éviter les fuites mémoire
 */
export function cleanupGrid(scene) {
    Object.values(gridHelpers).forEach(({ mainGrid, subGrid, centerLineX, centerLineY }) => {
        // Disposer les grilles
        if (mainGrid) {
            mainGrid.geometry?.dispose();
            mainGrid.material?.dispose();
            scene.remove(mainGrid);
        }
        if (subGrid) {
            subGrid.geometry?.dispose();
            subGrid.material?.dispose();
            scene.remove(subGrid);
        }
        
        // Disposer les lignes centrales
        if (centerLineX) {
            centerLineX.geometry?.dispose();
            centerLineX.material?.dispose();
            scene.remove(centerLineX);
        }
        if (centerLineY) {
            centerLineY.geometry?.dispose();
            centerLineY.material?.dispose();
            scene.remove(centerLineY);
        }
    });
    
    gridHelpers = {};
}

export function initPosition(){
    for (let version = 0; version < getTimes().length; version++ ) {
        Store.dispatch(setKeyframeTo(version))
    }
}

export function computeGridPosition(scene){

        const teethGroup = new THREE.Group();
        teethGroup.name = "Tooth_Group_Step_";

        scene.traverse((child) => {
            if (child.name.startsWith("0_Tooth")) {
                const clonedChild = child.clone();
                let nameParts = child.name.split("_");
                clonedChild.name = "0_FactiveTooth_" + nameParts[2] + "_Step_";
                teethGroup.add(clonedChild);
            }
        });

        return teethGroup;
}

export async function getMaxGridPosition(scene){

    initPosition();
    await nextRenderTick();

    const Animation = Store.getState().AnimationReducer;
    let planeJaw = null
    if (Animation.metas.pointPlanesJaw){
        planeJaw = Animation.metas.pointPlanesJaw
    }
    else {return null;}
    const teethCombinedGroup = new THREE.Group();




    for (let version = 0; version < getTimes().length; version++ ) {
        Store.dispatch(setKeyframeTo(version))
        teethCombinedGroup.add(computeGridPosition(scene));
        await nextRenderTick();
    }
    await nextRenderTick();
    const importedMeshesGroup = scene.getObjectByName("importedMeshes");
    if (importedMeshesGroup) {
        stateSetup.setupTeethGroup = cloneTeethGroup(importedMeshesGroup, "Setup", "setup");
        stateSetup.setupTeethGroup.children.forEach(clonedChild => {
            if (clonedChild.userData.jawSide === 'L') {
                threeEngine.addToAllMandiStuff(clonedChild);
            }
        });
        const scene = threeEngine.getScene();
        if (scene) {
            scene.add(stateSetup.setupTeethGroup)
        }
        createOverlayTeeth(importedMeshesGroup, "Setup", toothSetupMaterial);
    }
    await nextRenderTick();
    Store.dispatch(setKeyframeToBegin());
    attachTeethToMandiGroup();


    const toothBox = new THREE.Box3().setFromObject(teethCombinedGroup);

    const center = new THREE.Vector3();
    toothBox.getCenter(center);

    /* Récupération de la taille de notre box*/
    const size = new THREE.Vector3();
    toothBox.getSize(size);


    return {
        top: new THREE.Vector3(planeJaw[0], center.y, center.z + size.z / 2 + 0.1), // Face supérieure
        bottom: new THREE.Vector3(planeJaw[0], center.y, center.z - size.z / 2 - 0.1),  // Face inférieure
        left: new THREE.Vector3(center.x - size.x / 2 - 0.1, center.y, planeJaw[2]),  // Face gauche
        right: new THREE.Vector3(center.x + size.x / 2 + 0.1, center.y,  planeJaw[2]), // Face droite
        back: new THREE.Vector3(planeJaw[0], center.y + size.y / 2 + 0.1,  planeJaw[2]),   // Face arrière
        front: new THREE.Vector3(planeJaw[0], center.y - size.y / 2 - 0.1,  planeJaw[2]) // Face devant
    };

}
